/**
 * Controlled add/edit-event dialog. Calendar page opens it from
 * dateClick / select / eventClick callbacks.
 *
 * UX choices (from caregiver feedback during the trial):
 *   * Event vs Reminder is a two-radio choice. Events appear in the
 *     daily summary; reminders only fire when the button is pressed
 *     inside their time window. All-day events are always Events.
 *   * Date inputs are contextual: a single Date for timed events,
 *     a Start/End date pair for all-day events (multi-day all-day
 *     is the realistic use case).
 *   * `includeFromDaysBefore` ("Mention from N days before") lives
 *     under More options, alongside the notifications list.
 *   * `notifications[]` are pre-event auto-fires (Google-Calendar
 *     style). Capped at 5 in the UI to keep the list manageable;
 *     mirrored in the schema and Firestore rules.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { upsertEvent, deleteEvent, excludeOccurrence } from '@/lib/firestore';
import { addDays } from '@/lib/dateUtil';
import { wallClockToInstant } from '@/lib/timeZone';
import { track } from '@/lib/analytics';
import type { EventDoc, EventNotification } from '@/schemas';

/** Controlled `type="number"` input that doesn't fight the user.
 *
 * The default HTMLInputElement+React combo coerces empty input to 0
 * on every keystroke, so "select-all, type new value" snaps to 0
 * mid-edit and the user can't actually replace the number. We keep
 * the input's textual value in local state and only commit a parsed
 * number to the parent when input is non-empty. On blur, empty /
 * unparseable text reverts to the committed value (never silently 0). */
function NumberInput({
  value,
  onChange,
  min = 0,
  max,
  ...rest
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
} & Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
>) {
  const [text, setText] = useState<string>(String(value));
  // Resync text when the parent's value changes — covers programmatic
  // resets and submit-time clamping. Same-value setState is a no-op
  // so it's safe to fire on every parent update.
  useEffect(() => {
    setText(String(value));
  }, [value]);
  return (
    <Input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t === '') return;
        const n = Number(t);
        if (Number.isNaN(n)) return;
        const clamped =
          max != null ? Math.min(max, Math.max(min, n)) : Math.max(min, n);
        onChange(clamped);
      }}
      onBlur={() => {
        // On blur, snap the display back to the committed value. A field
        // left empty or non-numeric (usually cleared mid-edit) therefore
        // REVERTS to what it was — it never silently persists as 0. Since
        // several offsets treat 0 as a real value ("fire at start",
        // "0 days"), an accidental clear must not be mistaken for choosing
        // 0; the caregiver has to type an explicit number (incl. "0") to
        // change it. Also catches the "typed 9999, clamped to max 1440"
        // case where useEffect didn't refire.
        setText(String(value));
      }}
    />
  );
}

/** Notification offset bounds, in minutes.
 * - 0 means "fire at event start", useful for reminder-kind events.
 * - 1440 (24 h) is the cap — beyond that the spoken text
 *   ("at half past three") becomes ambiguous between days.
 *   Caregivers wanting multi-day advance notice should use
 *   `includeFromDaysBefore` to put the event in the summary.
 * - For kind='reminder' events, the only consistent offset is 0
 *   (otherwise pressing after the notification fires would produce
 *   a summary that doesn't mention the event). The reminder UI
 *   collapses to a single "Notify at start" checkbox; only event-
 *   kind events get the multi-offset UI. */
const MIN_NOTIFICATION_MINUTES = 0;
const MAX_NOTIFICATION_MINUTES = 1440;
/** Mirrored in `eventSchema.notifications.max(...)` and in
 * `firestore.rules`. Update all three together. */
const MAX_NOTIFICATIONS_PER_EVENT = 5;

export interface EventFormInitial {
  /** If null, dialog is in "create" mode. */
  eventId: string | null;
  /** 'event' (default) appears in the daily summary at its start time;
   * 'interval' appears in the summary as a time range (delivery slots);
   * 'reminder' is only spoken when the current moment is inside the
   * event's [start, end] window. */
  kind: 'event' | 'interval' | 'reminder';
  title: string;
  /** YYYY-MM-DD — start day (inclusive). */
  startDate: string;
  /** YYYY-MM-DD — end day (inclusive). Equal to startDate for single-day events. */
  endDate: string;
  /** HH:MM (24h) */
  startTime: string;
  /** HH:MM (24h) — empty = no end */
  endTime: string;
  allDay: boolean;
  /** String for the controlled input; empty = unset. */
  includeFromDaysBefore: string;
  notifications: EventNotification[];
  recurrence: 'none' | 'daily' | 'weekly';
  /**
   * YYYY-MM-DD — when the dialog was opened from a click on a
   * specific calendar occurrence, this is that occurrence's local
   * date. Lets the per-occurrence delete UI know which day to add
   * to `excludedDates`. Undefined when opening from the +Add button
   * or for non-recurring events where it'd be unused anyway.
   */
  occurrenceDate?: string;
}

const TODAY = new Date().toISOString().slice(0, 10);
export const EMPTY_FORM: EventFormInitial = {
  eventId: null,
  kind: 'event',
  title: '',
  startDate: TODAY,
  endDate: TODAY,
  startTime: '09:00',
  endTime: '',
  allDay: false,
  // '0' default (vs empty) so the inline UI under More options
  // shows a concrete number, not an ambiguous-looking empty box.
  // The renderer treats 0 the same as absent — no countdown.
  includeFromDaysBefore: '0',
  notifications: [],
  recurrence: 'none',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: EventFormInitial;
  aid: string;
  cid: string;
  /** The ELDER's account timezone. Form date/time fields are wall-clock in
   * this zone (not the caregiver's browser), so save converts through it. */
  timeZone: string;
}

/** One-line description of each event kind, shown as the Type field's
 * hint and swapped as the dropdown selection changes. */
const KIND_HINTS: Record<'event' | 'interval' | 'reminder', string> = {
  event: 'Announced at its start time, in the daily summary.',
  interval: 'Announced as a time range — for delivery slots or appointments.',
  reminder: "Spoken in your own words only while it's happening — not in the summary.",
};

export function EventFormDialog({ open, onOpenChange, initial, aid, cid, timeZone }: Props) {
  const [form, setForm] = useState<EventFormInitial>(() => withDefaultEnd(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "More options" disclosure for the advanced fields (days-before
  // countdown + notifications). Auto-opens when editing an event
  // that already has values in those fields, so the caregiver
  // doesn't see "saved data disappeared" surprise.
  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    () =>
      Boolean(initial.includeFromDaysBefore && initial.includeFromDaysBefore !== '0')
      || initial.notifications.length > 0,
  );

  useEffect(() => {
    if (open) {
      setForm(withDefaultEnd(initial));
      setError(null);
      setShowAdvanced(
        Boolean(initial.includeFromDaysBefore && initial.includeFromDaysBefore !== '0')
        || initial.notifications.length > 0,
      );
    }
  }, [open, initial]);

  const isEdit = initial.eventId !== null;
  const isRecurring = form.recurrence !== 'none';

  return (
    // While a save/delete is in flight, ignore close requests (overlay
    // tap, Escape). A retrying save can take several seconds on a flaky
    // connection; letting the dialog dismiss mid-save meant a late
    // failure had nowhere to surface its error — the caregiver walked
    // away believing the change saved.
    <Dialog open={open} onOpenChange={(next) => { if (!next && busy) return; onOpenChange(next); }}>
      <DialogContent className="max-w-lg">
        <DialogTitle>{isEdit ? 'Edit event' : 'New event'}</DialogTitle>
        <DialogDescription className="sr-only">
          {isEdit
            ? 'Edit the title, date, time, and recurrence of this event, or delete it.'
            : 'Add a new event or reminder to your calendar.'}
        </DialogDescription>
        <form
          className="mt-5 flex flex-col gap-5"
          onSubmit={async (event) => {
            event.preventDefault();
            // Timed events always carry an end — no point-in-time events. A blank
            // end (e.g. the user cleared the field) defaults to start + 1h here, a
            // backstop to withDefaultEnd/onBlur so a cleared end can neither save a
            // point event nor (with `required` gone) silently no-op Save.
            const effectiveEndTime =
              form.allDay || form.endTime
                ? form.endTime
                : shiftClockTime(form.startTime, DEFAULT_EVENT_DURATION_MIN);
            if (form.allDay) {
              if (form.endDate < form.startDate) {
                setError('End date is before start date.');
                return;
              }
            } else if (effectiveEndTime && effectiveEndTime <= form.startTime) {
              setError(
                effectiveEndTime === form.startTime
                  ? 'End time is the same as start time.'
                  : 'End time is before start time.',
              );
              return;
            }
            setBusy(true);
            setError(null);
            try {
              const startsAt = wallClockToInstant(
                form.startDate,
                form.allDay ? '00:00' : form.startTime,
                timeZone,
              );
              // iCal-style exclusive end for all-day events.
              const endsAt = form.allDay
                ? wallClockToInstant(addDays(form.endDate, 1), '00:00', timeZone)
                : effectiveEndTime
                  ? wallClockToInstant(form.startDate, effectiveEndTime, timeZone)
                  : null;
              // All-day events are always agenda items — the
              // reminder-kind semantics only make sense for timed
              // events with a [startsAt, endsAt] window.
              const finalKind: 'event' | 'interval' | 'reminder' = form.allDay
                ? 'event'
                : form.kind;
              // Belt-and-braces clamp: for reminder-kind events, any
              // notification offset other than 0 would fire outside
              // the event window and produce an inconsistent press
              // follow-up. Force to 0 on submit. (Interval is a summary
              // event, so it keeps multi-offset notifications like
              // 'event'.)
              const finalNotifications = form.notifications.map((r) =>
                finalKind === 'reminder'
                  ? { ...r, minutesBefore: 0 }
                  : r,
              );
              const payload: Omit<EventDoc, 'createdBy' | 'createdAt' | 'updatedAt'> = {
                kind: finalKind,
                title: form.title.trim(),
                startsAt: Timestamp.fromDate(startsAt),
                endsAt: endsAt ? Timestamp.fromDate(endsAt) : undefined,
                allDay: form.allDay,
                includeFromDaysBefore: form.includeFromDaysBefore
                  ? Number(form.includeFromDaysBefore)
                  : undefined,
                notifications: finalNotifications.length > 0 ? finalNotifications : undefined,
                recurrence: form.allDay ? 'none' : form.recurrence,
              };
              await upsertEvent(aid, cid, initial.eventId ?? null, payload);
              if (!initial.eventId) {
                track('calendar_event_added', { kind: finalKind, recurrence: payload.recurrence });
              }
              onOpenChange(false);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Save failed.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {isRecurring && isEdit && (
            <div className="rounded-md bg-paper-alt px-3 py-2 text-xs text-ink-soft">
              This is a recurring event — your changes apply to every occurrence.
            </div>
          )}

          <Field label="Title" htmlFor="title">
            <Input
              id="title"
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Lunch with Mary"
            />
          </Field>

          {!form.allDay && (
            <Field label="Type" htmlFor="kind" hint={KIND_HINTS[form.kind]}>
              <select
                id="kind"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={form.kind}
                onChange={(e) => {
                  const kind = e.target.value as 'event' | 'interval' | 'reminder';
                  setForm((f) => {
                    let notifications = f.notifications;
                    if (kind === 'reminder') {
                      // Reminder shows a single "Notify at start" checkbox;
                      // collapse any event-style offsets to one at offset 0.
                      notifications =
                        f.notifications.length > 0 ? [{ minutesBefore: 0 }] : [];
                    } else if (f.kind === 'reminder') {
                      // Leaving Reminder: drop the offset-0 stub the
                      // checkbox added so it doesn't submit as a real
                      // fire-at-start notification.
                      notifications =
                        f.notifications.length === 1
                        && f.notifications[0]?.minutesBefore === 0
                          ? []
                          : f.notifications;
                    }
                    return { ...f, kind, notifications };
                  });
                }}
              >
                <option value="event">Event (at its start time)</option>
                <option value="interval">Time window (a range — e.g. a delivery slot)</option>
                <option value="reminder">Reminder (only while it's happening)</option>
              </select>
            </Field>
          )}

          {form.allDay ? (
            // All-day: date range only.
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date" htmlFor="startDate">
                <Input
                  id="startDate"
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setForm((f) => ({
                      ...f,
                      startDate: newStart,
                      endDate:
                        f.endDate < newStart || f.endDate === f.startDate
                          ? newStart
                          : f.endDate,
                    }));
                  }}
                />
              </Field>
              <Field
                label="End date"
                htmlFor="endDate"
                hint={
                  form.endDate !== form.startDate
                    ? 'Multi-day event'
                    : 'Same as start for a single-day event'
                }
              >
                <Input
                  id="endDate"
                  type="date"
                  required
                  min={form.startDate}
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </Field>
            </div>
          ) : (
            // Timed: date + start + end in one 3-col row on desktop,
            // stacking on mobile (date inputs are wider than time
            // inputs, so a single line gets cramped under ~500px).
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Date" htmlFor="startDate">
                <Input
                  id="startDate"
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      startDate: e.target.value,
                      endDate: e.target.value,
                    })
                  }
                />
              </Field>
              <Field label="Start time" htmlFor="startTime">
                <Input
                  id="startTime"
                  type="time"
                  required
                  value={form.startTime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setForm((f) => {
                      // Preserve duration: dragging the start time shifts the
                      // end time by the same delta (like Google/Apple Calendar),
                      // so you only adjust one field. Only when both are set.
                      if (!f.startTime || !f.endTime || !newStart) {
                        return { ...f, startTime: newStart };
                      }
                      const delta = toMinutesOfDay(newStart) - toMinutesOfDay(f.startTime);
                      return {
                        ...f,
                        startTime: newStart,
                        endTime: shiftClockTime(f.endTime, delta),
                      };
                    });
                  }}
                />
              </Field>
              {/* Every timed event has an end (standard calendar model) — no
                  point-in-time events. The end defaults to start + 1h on open
                  (withDefaultEnd) and as the start-time handler drags it. If the
                  user CLEARS it, we re-default: on blur (so they see the value)
                  and again at submit (the backstop). No `required` — that only
                  silently blocked Save on a manually-cleared field; the submit
                  default now guarantees a valid end. */}
              <Field label="End time" htmlFor="endTime">
                <Input
                  id="endTime"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setForm((f) => ({
                        ...f,
                        endTime: shiftClockTime(f.startTime, DEFAULT_EVENT_DURATION_MIN),
                      }));
                    }
                  }}
                />
              </Field>
            </div>
          )}

          <CheckboxRow
            id="allDay"
            checked={form.allDay}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                allDay: v,
                // All-day events can't be reminders (the reminder
                // semantics need a [start, end] time window). Reset
                // kind so a hidden 'reminder' value doesn't reappear
                // and silently collapse the notifications UI when
                // the user toggles all-day back off.
                kind: v ? 'event' : f.kind,
              }))
            }
            label="All day"
          />

          {!form.allDay && (
            <Field label="Repeats" htmlFor="recurrence">
              <select
                id="recurrence"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={form.recurrence}
                onChange={(e) =>
                  setForm({
                    ...form,
                    recurrence: e.target.value as 'none' | 'daily' | 'weekly',
                  })
                }
              >
                <option value="none">No repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </Field>
          )}

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="self-start text-sm text-accent-700 hover:underline"
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? '▾ Fewer options' : '▸ More options'}
          </button>

          {showAdvanced && (
            <>
              {(form.allDay || form.kind !== 'reminder') && (
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="includeFromDaysBefore"
                    className="flex flex-wrap items-center gap-2 text-sm text-ink"
                  >
                    <span>Mention in summary from</span>
                    <NumberInput
                      id="includeFromDaysBefore"
                      min={0}
                      max={365}
                      value={Number(form.includeFromDaysBefore) || 0}
                      onChange={(n) =>
                        setForm({ ...form, includeFromDaysBefore: String(n) })
                      }
                      className="w-16"
                    />
                    <span>days before.</span>
                  </label>
                  <p className="text-xs text-ink-muted">
                    0 = only on the day itself. Higher values add a countdown
                    line ("In 3 days, …") in the days leading up to the event.
                  </p>
                </div>
              )}

              <NotificationsSection
                notifications={form.notifications}
                onChange={(notifications) => setForm({ ...form, notifications })}
                includeFromDaysBefore={
                  form.includeFromDaysBefore ? Number(form.includeFromDaysBefore) : 0
                }
                startDate={form.startDate}
                startTime={form.startTime}
                allDay={form.allDay}
                kind={form.allDay ? 'event' : form.kind}
                aid={aid}
              />
            </>
          )}

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <div className="flex justify-between">
            {isEdit ? (
              <DeleteControl
                eventId={initial.eventId}
                isRecurring={isRecurring}
                occurrenceDate={initial.occurrenceDate}
                aid={aid}
                cid={cid}
                busy={busy}
                onBusy={setBusy}
                onError={setError}
                onDone={() => onOpenChange(false)}
              />
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !form.title.trim()}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Pre-event notifications. For kind='event', a multi-row UI (any
 * number of offsets, each with its own minutes/hours picker). For
 * kind='reminder', collapses to a single "Notify at start" checkbox
 * — the only consistent fire moment when the event isn't mentioned
 * in the daily summary is `minutesBefore: 0`. Renders a soft warning
 * when a notification fires on a calendar day outside the summary's
 * mention window. */
function NotificationsSection({
  notifications,
  onChange,
  includeFromDaysBefore,
  startDate,
  startTime,
  allDay,
  kind,
  aid,
}: {
  notifications: EventNotification[];
  onChange: (next: EventNotification[]) => void;
  includeFromDaysBefore: number;
  startDate: string;
  startTime: string;
  allDay: boolean;
  kind: 'event' | 'interval' | 'reminder';
  /** Account ID — used for the "Voice settings" link below. */
  aid: string;
}) {
  function addRow() {
    onChange([...notifications, { minutesBefore: 30 }]);
  }
  function removeRow(index: number) {
    onChange(notifications.filter((_, i) => i !== index));
  }
  function updateRow(index: number, patch: Partial<EventNotification>) {
    onChange(notifications.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  // Reminder-kind events: a notification (if present) always fires at
  // offset 0. Collapse the whole row UI to a single boolean toggle.
  if (kind === 'reminder') {
    const enabled = notifications.length > 0;
    return (
      <div className="flex flex-col gap-3 rounded-md border border-paper-border bg-paper-alt px-3 py-3">
        <span className="text-sm font-medium text-ink">Notification</span>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) =>
              onChange(e.target.checked ? [{ minutesBefore: 0 }] : [])
            }
          />
          <span>Flash (and speak, if configured) at the start of the window</span>
        </label>
        <p className="text-xs text-ink-muted">
          What the button says (if anything) is configured under{' '}
          <Link
            to={`/accounts/${aid}/voice`}
            className="text-accent-700 underline-offset-2 hover:underline"
          >
            Voice → Notification prompt
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-paper-border bg-paper-alt px-3 py-3">
      <span className="text-sm font-medium text-ink">Notifications before this event</span>
      {notifications.length > 0 && (
        <div className="flex flex-col gap-2">
          {notifications.map((r, i) => (
            <NotificationRow
              key={i}
              notification={r}
              onChange={(patch) => updateRow(i, patch)}
              onRemove={() => removeRow(i)}
              // Warn when an audible notification fires further out
              // than the daily summary mentions the event: the elder
              // presses after hearing it and gets a summary that
              // doesn't include the event. Only meaningful when the
              // account-wide notificationPrompt template is non-
              // silent, which we can't determine here — show the
              // warning regardless. Caregivers using silent
              // notifications can ignore it.
              promptWarning={notificationFiresOutsideSummaryWindow({
                minutesBefore: r.minutesBefore,
                includeFromDaysBefore,
                startDate,
                startTime,
                allDay,
              })}
            />
          ))}
        </div>
      )}
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addRow}
          disabled={notifications.length >= MAX_NOTIFICATIONS_PER_EVENT}
        >
          + Add notification
        </Button>
        {notifications.length >= MAX_NOTIFICATIONS_PER_EVENT && (
          <span className="ml-2 text-xs text-ink-muted">
            Up to {MAX_NOTIFICATIONS_PER_EVENT} notifications per event.
          </span>
        )}
      </div>
      <p className="text-xs text-ink-muted">
        What the button says when a notification fires (or whether it
        speaks at all) is configured under{' '}
        <Link
          to={`/accounts/${aid}/voice`}
          className="text-accent-700 underline-offset-2 hover:underline"
        >
          Voice → Notification prompt
        </Link>
        .
      </p>
    </div>
  );
}

/** True when a notification's fire time lands on a calendar day
 * before the summary's mention window opens — i.e. an auto-press at
 * that moment would produce a summary that doesn't actually mention
 * the imminent event.
 *
 * Summary mentions the event on days where `daysUntilEvent <=
 * includeFromDaysBefore`. So the notification is "covered" iff the
 * calendar-day gap between fireTime and startsAt is at most
 * includeFromDaysBefore.
 *
 * Computed in the browser's local tz — close enough for a UI hint
 * (the elder's account tz handles the real dispatch). */
function notificationFiresOutsideSummaryWindow({
  minutesBefore,
  includeFromDaysBefore,
  startDate,
  startTime,
  allDay,
}: {
  minutesBefore: number;
  includeFromDaysBefore: number;
  startDate: string;
  startTime: string;
  allDay: boolean;
}): boolean {
  const [y, m, d] = startDate.split('-').map(Number);
  if (!y || !m || !d) return false;  // form not yet valid; suppress warning
  let hh = 0;
  let mm = 0;
  if (!allDay && startTime) {
    const parts = startTime.split(':').map(Number);
    hh = parts[0] ?? 0;
    mm = parts[1] ?? 0;
  }
  const startsAt = new Date(y, m - 1, d, hh, mm, 0, 0);
  const fireAt = new Date(startsAt.getTime() - minutesBefore * 60_000);
  const startDay = new Date(startsAt.getFullYear(), startsAt.getMonth(), startsAt.getDate());
  const fireDay = new Date(fireAt.getFullYear(), fireAt.getMonth(), fireAt.getDate());
  const daysDiff = Math.round(
    (startDay.getTime() - fireDay.getTime()) / 86_400_000,
  );
  return daysDiff > Math.max(0, includeFromDaysBefore);
}

function NotificationRow({
  notification,
  onChange,
  onRemove,
  promptWarning,
}: {
  notification: EventNotification;
  onChange: (patch: Partial<EventNotification>) => void;
  onRemove: () => void;
  promptWarning: boolean;
}) {
  // Display unit is UI-only — schema always stores minutesBefore.
  // Initial pick: hours when the saved value is a clean hour
  // multiple (≥60 and divisible). Misclassifies "60 minutes
  // intended"-as-hours which is harmless (same duration, just a
  // different label). Caregiver can always switch back.
  const initialUnit: 'minutes' | 'hours' =
    notification.minutesBefore >= 60 && notification.minutesBefore % 60 === 0
      ? 'hours'
      : 'minutes';
  const [unit, setUnit] = useState<'minutes' | 'hours'>(initialUnit);
  const displayValue =
    unit === 'hours'
      ? Math.round(notification.minutesBefore / 60)
      : notification.minutesBefore;
  const maxForUnit = unit === 'hours' ? 24 : MAX_NOTIFICATION_MINUTES;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <NumberInput
          min={MIN_NOTIFICATION_MINUTES}
          max={maxForUnit}
          value={displayValue}
          onChange={(n) =>
            onChange({
              minutesBefore: unit === 'hours' ? n * 60 : n,
            })
          }
          className="w-20"
          aria-label="Time before event"
        />
        <select
          className="h-10 rounded-lg border border-paper-border bg-white px-2 text-sm text-ink"
          value={unit}
          onChange={(e) => {
            const next = e.target.value as 'minutes' | 'hours';
            if (next === unit) return;
            setUnit(next);
            if (next === 'hours') {
              // Snap minutes → hours: round to the nearest hour so the
              // displayed integer is what the caregiver intends (and
              // matches the stored minutes). A non-zero offset must
              // never snap DOWN to 0 — e.g. 25 min would round to
              // "0 hours", silently converting a real advance warning
              // into fire-at-start. Floor such values at 1 hour.
              const hours = Math.round(notification.minutesBefore / 60);
              const floor = notification.minutesBefore > 0 ? 1 : 0;
              const snapped = Math.min(24, Math.max(floor, hours)) * 60;
              if (snapped !== notification.minutesBefore) {
                onChange({ minutesBefore: snapped });
              }
            }
            // hours → minutes: existing minutes value is already an
            // exact hour multiple, no conversion needed.
          }}
          aria-label="Time unit"
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
        </select>
        <span className="text-xs text-ink-soft">before event</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="ml-auto text-red-700 hover:bg-red-50"
          aria-label="Remove notification"
        >
          ✕
        </Button>
      </div>
      {promptWarning && (
        <p className="text-xs text-amber-700">
          This notification fires further out than the summary mentions
          the event. Increase "Mention in summary from N days before"
          so the summary the elder hears on press actually includes
          this event.
        </p>
      )}
    </div>
  );
}

interface DeleteControlProps {
  eventId: string | null;
  isRecurring: boolean;
  occurrenceDate: string | undefined;
  aid: string;
  cid: string;
  busy: boolean;
  onBusy: (b: boolean) => void;
  onError: (msg: string | null) => void;
  onDone: () => void;
}

/** Delete UI for an existing event. Two paths:
 *  - Non-recurring (or no occurrenceDate context): single confirm,
 *    deletes the doc.
 *  - Recurring with an occurrenceDate: small two-button chooser —
 *    "Just this occurrence (5 May)" appends to `excludedDates`, "All
 *    occurrences" deletes the doc.
 *
 *  Confirmation is rendered inline (replacing the Delete button area)
 *  rather than via window.confirm, so it stays inside our own modal
 *  styling and doesn't lock the page on slow native dialogs.
 */
function DeleteControl(props: DeleteControlProps) {
  const { eventId, isRecurring, occurrenceDate, aid, cid, busy, onBusy, onError, onDone } = props;
  const [showChooser, setShowChooser] = useState(false);
  const [confirmingSeries, setConfirmingSeries] = useState(false);

  if (!eventId) return null;

  const occurrenceLabel = occurrenceDate
    ? new Date(`${occurrenceDate}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short',
      })
    : null;

  async function deleteSeries() {
    if (!eventId) return;
    onBusy(true);
    try {
      await deleteEvent(aid, cid, eventId);
      onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      onBusy(false);
    }
  }

  async function excludeThisOccurrence() {
    if (!eventId || !occurrenceDate) return;
    onBusy(true);
    try {
      await excludeOccurrence(aid, cid, eventId, occurrenceDate);
      onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      onBusy(false);
    }
  }

  if (confirmingSeries) {
    const prompt = isRecurring
      ? 'Delete this recurring event and every occurrence?'
      : 'Delete this event?';
    return (
      <div className="flex flex-col gap-2">
        <span className="text-sm text-ink">{prompt}</span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => setConfirmingSeries(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={busy}
            onClick={() => void deleteSeries()}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    );
  }

  // Non-recurring: single confirm path.
  if (!isRecurring || !occurrenceDate) {
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={busy}
        className="text-red-700 hover:bg-red-50"
        onClick={() => setConfirmingSeries(true)}
      >
        Delete
      </Button>
    );
  }

  // Recurring with a known occurrence: chooser.
  if (!showChooser) {
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={busy}
        className="text-red-700 hover:bg-red-50"
        onClick={() => setShowChooser(true)}
      >
        Delete…
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="ghost"
        disabled={busy}
        className="text-red-700 hover:bg-red-50"
        onClick={() => void excludeThisOccurrence()}
      >
        Just this occurrence ({occurrenceLabel})
      </Button>
      <Button
        type="button"
        variant="ghost"
        disabled={busy}
        className="text-red-700 hover:bg-red-50"
        onClick={() => setConfirmingSeries(true)}
      >
        All occurrences
      </Button>
    </div>
  );
}

/** Tiny inline help icon. Native `title` tooltip — works on hover and
 * mobile long-press without any extra deps. */
function HelpIcon({ text }: { text: string }) {
  return (
    <span
      className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-paper-border text-[10px] font-semibold text-ink-muted"
      title={text}
      aria-label={text}
    >
      ?
    </span>
  );
}

/** Checkbox + label in one focusable row, with an optional help icon.
 * Wider hit target than the bare native control + brand-coloured tick. */
function CheckboxRow({
  id,
  checked,
  onChange,
  label,
  help,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  help?: string;
}) {
  return (
    <label
      htmlFor={id}
      className="inline-flex cursor-pointer items-center gap-2 select-none text-sm text-ink"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-paper-border accent-accent-600"
      />
      <span>{label}</span>
      {help && <HelpIcon text={help} />}
    </label>
  );
}

/** Minutes since midnight for an "HH:MM" clock string. */
function toMinutesOfDay(time: string): number {
  const [hh, mm] = time.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
}

/** Shift an "HH:MM" clock string by `deltaMin`, clamped to [00:00, 23:59]
 * (events are single-day, so the end can't roll past midnight). */
function shiftClockTime(time: string, deltaMin: number): string {
  const total = Math.max(0, Math.min(23 * 60 + 59, toMinutesOfDay(time) + deltaMin));
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Default duration, in minutes, for a new event whose end wasn't set by a
 * calendar drag/click (i.e. the "+ Add event" path). Matches the 1-hour
 * default of standard calendars. */
const DEFAULT_EVENT_DURATION_MIN = 60;

/** Ensure a timed event has an end time. End time is required, but the user
 * must never face a *blank* required field (that silently blocks Save), so a
 * new/legacy event with no end defaults to start + 1h. All-day events don't
 * use an end time. */
function withDefaultEnd(init: EventFormInitial): EventFormInitial {
  if (init.allDay || init.endTime) return init;
  return { ...init, endTime: shiftClockTime(init.startTime, DEFAULT_EVENT_DURATION_MIN) };
}

